import { useEffect, useRef } from 'react';
import { Box, Paper, CircularProgress, Alert } from '@mui/material';
import { useRpcClientCall } from '@/providers/backendClient';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface ReportsViewerProps {
  filePath: string;
  currentPath: string;
}

/**
 * Resolves a relative markdown link to an absolute path
 * @param linkPath - The link path from the markdown (e.g., "./01-problem.md", "../README.md", "01-problem.md")
 * @param currentFilePath - The current file's path (e.g., "2025-12-05-1-ExpiredAuth0TokensConnectivityIssue/README.md")
 * @returns The resolved absolute path or null if it's not a markdown link
 */
const resolveMarkdownLink = (
  linkPath: string,
  currentFilePath: string,
): string | null => {
  // Only handle .md and .markdown files
  if (!linkPath.match(/\.(md|markdown)$/i)) {
    return null;
  }

  // Remove query strings and anchors
  const cleanPath = linkPath.split('?')[0].split('#')[0];

  // Get the directory of the current file
  const currentDir = currentFilePath.includes('/')
    ? currentFilePath.substring(0, currentFilePath.lastIndexOf('/'))
    : '';

  // Handle different link formats
  let resolvedPath: string;

  if (cleanPath.startsWith('./')) {
    // Relative to current directory: ./01-problem.md
    resolvedPath = currentDir
      ? `${currentDir}/${cleanPath.slice(2)}`
      : cleanPath.slice(2);
  } else if (cleanPath.startsWith('../')) {
    // Parent directory: ../README.md
    const parts = currentDir.split('/');
    let relativePath = cleanPath;
    while (relativePath.startsWith('../')) {
      parts.pop();
      relativePath = relativePath.slice(3);
    }
    resolvedPath =
      parts.length > 0
        ? `${parts.join('/')}/${relativePath}`
        : relativePath;
  } else if (cleanPath.startsWith('/')) {
    // Absolute from root: /2025-12-05-1-ExpiredAuth0TokensConnectivityIssue/README.md
    resolvedPath = cleanPath.slice(1);
  } else {
    // Relative to current directory: 01-problem.md
    resolvedPath = currentDir
      ? `${currentDir}/${cleanPath}`
      : cleanPath;
  }

  return resolvedPath;
};

export const ReportsViewer = ({
  filePath,
  currentPath,
}: ReportsViewerProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useRpcClientCall(
    'PUBLIC_ADMIN_reports_get',
    [filePath],
    {},
  );

  // Intercept clicks on markdown links
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const dataResult = data as
      | { success?: boolean; content?: string }
      | undefined;
    if (!dataResult?.success) {
      return;
    }

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (!link || !link.href) {
        return;
      }

      // Check if it's an internal markdown link
      try {
        const url = new URL(link.href);
        // If it's a data URL or external link, let it behave normally
        if (
          url.protocol !== 'data:' &&
          (url.origin !== window.location.origin ||
            url.pathname.startsWith('/reports'))
        ) {
          // This might be an internal link we should intercept
          const linkPath = decodeURIComponent(url.pathname);

          // Extract the path after /reports/ if it exists
          if (linkPath.startsWith('/reports/')) {
            e.preventDefault();
            e.stopPropagation();
            navigate(linkPath);
            return;
          }
        }
      } catch {
        // If URL parsing fails, check if it's a relative link
        const href = link.getAttribute('href');
        if (
          href &&
          (href.startsWith('./') ||
            href.startsWith('../') ||
            href.endsWith('.md') ||
            href.endsWith('.markdown'))
        ) {
          const resolvedPath = resolveMarkdownLink(href, currentPath);
          if (resolvedPath) {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/reports/${encodeURIComponent(resolvedPath)}`);
            return;
          }
        }
      }
    };

    container.addEventListener('click', handleLinkClick);
    return () => {
      container.removeEventListener('click', handleLinkClick);
    };
  }, [data, currentPath, navigate]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load report:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  const dataResult = data as
    | { success: boolean; content?: string; error?: string }
    | undefined;

  if (!dataResult?.success || !dataResult?.content) {
    return (
      <Alert severity="warning">
        {dataResult?.error || 'Report not found'}
      </Alert>
    );
  }

  return (
    <Paper
      sx={{
        p: 0,
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'rgb(13, 17, 23)',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          padding: '32px',
          backgroundColor: 'rgb(13, 17, 23)',
          color: '#c9d1d9',
          '& > *': {
            marginBottom: '16px',
          },
          '& > *:last-child': {
            marginBottom: 0,
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '24px',
            marginBottom: '16px',
            color: '#c9d1d9',
            fontWeight: 600,
          },
          '& h1': {
            fontSize: '2em',
            borderBottom: '1px solid #30363d',
            paddingBottom: '0.3em',
          },
          '& h2': {
            fontSize: '1.5em',
            borderBottom: '1px solid #30363d',
            paddingBottom: '0.3em',
          },
          '& p': {
            marginBottom: '16px',
            lineHeight: 1.6,
          },
          '& ul, & ol': {
            marginBottom: '16px',
            paddingLeft: '24px',
          },
          '& li': {
            marginBottom: '8px',
          },
          '& a': {
            color: '#58a6ff',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& code': {
            backgroundColor: 'rgba(110, 118, 129, 0.4)',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontSize: '85%',
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          },
          '& pre': {
            backgroundColor: '#161b22',
            padding: '16px',
            borderRadius: '6px',
            overflow: 'auto',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
          },
          '& blockquote': {
            padding: '0 1em',
            color: '#8b949e',
            borderLeft: '0.25em solid #30363d',
            margin: '16px 0',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '16px',
          },
          '& th, & td': {
            border: '1px solid #30363d',
            padding: '6px 13px',
          },
          '& th': {
            backgroundColor: '#161b22',
            fontWeight: 600,
          },
          '& tr:nth-of-type(2n)': {
            backgroundColor: '#161b22',
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize]]}
        >
          {dataResult.content}
        </ReactMarkdown>
      </Box>
    </Paper>
  );
};
